import * as React from "react";
import Paper from "@mui/material/Paper";
import moment from "moment";
import {
  Button,
  Divider,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Pagination,
  PaginationItem,
} from "@mui/material";
import {
  DataGrid,
  GridToolbar,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";
import API from "../../Api/api";
import { useSelector } from "react-redux";
import { message as feedback } from "antd";
import toast from "react-hot-toast";
const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "decimal",
  maximumFractionDigits: 2,
});
const columns = [
  { field: "code", headerName: "Asset Code", minWidth: 200 },
  {
    field: "previous_cost",
    headerName: "PREVIOUS COST",
    minWidth: 170,
    align: "right",
    valueFormatter: ({ value }) => currencyFormatter.format(value),
  },
  {
    field: "accum_depre",
    headerName: "ACCUM. DEPN.",
    minWidth: 200,
    align: "right",
    valueFormatter: ({ value }) => currencyFormatter.format(value),
  },
  {
    field: "carrying_amount",
    headerName: "CARRYING AMOUNT",
    minWidth: 170,
    align: "right",
    valueFormatter: ({ value }) => currencyFormatter.format(value),
  },
  {
    field: "revaluation_amount",
    headerName: "REVALUATION AMOUNT",
    minWidth: 200,
    align: "right",
    valueFormatter: ({ value }) => currencyFormatter.format(value),
  },
  {
    field: "profit_loss",
    headerName: "SURPLUS/DEFICIT",
    minWidth: 170,
    align: "right",
    valueFormatter: ({ value }) => currencyFormatter.format(value),
  },
  {
    field: "revaluation_date",
    headerName: "REVALUATION DATE",
    minWidth: 170,
    align: "right",
  },
];
function CustomPagination() {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  return (
    <Pagination
      color='primary'
      sx={{ bgcolor: "#ffffff" }}
      variant='outlined'
      shape='rounded'
      page={page + 1}
      count={pageCount}
      // @ts-expect-error
      renderItem={(props2) => <PaginationItem {...props2} disableRipple />}
      onChange={(event, value) => apiRef.current.setPage(value - 1)}
    />
  );
}
export default function AssetRevaluationReport() {
  const mode_state = useSelector((state) => state.mode);
  const [mode, setMode] = React.useState(mode_state);
  React.useEffect(() => {
    setMode(mode_state);
  }, [mode_state]);

  const [message, setMessage] = React.useState(false);
  const [data, setData] = React.useState([]);

  const [filterData, setFilterData] = React.useState({
    start_date: "",
    end_date: "",
  });
  React.useEffect(() => {
    const fetchData = async () => {
      setMessage(true);
      const res = await API.get(
        `/api/v1/company-assets/ppe/ppe-section?revalued[eq]=true&approved[eq]=true`
      );
      if (res.data[0]) {
        setData(res.data);
        setMessage(false);
      } else {
        setData([]);
        setMessage(false);
      }
    };
    fetchData();
  }, []);
  function createData(
    id,
    code,
    previous_cost,
    accum_depre,
    revaluation_amount,
    revaluation_date
  ) {
    const carrying_amount = previous_cost - accum_depre;
    const profit_loss = revaluation_amount - carrying_amount;
    return {
      id,
      code,
      previous_cost,
      accum_depre,
      carrying_amount,
      revaluation_amount,
      profit_loss,
      revaluation_date,
    };
  }
  // ! declaring of queries so that we can chain them together// Start
  const start = filterData.start_date
    ? `&revaluation_date[gte]=${filterData.start_date}`
    : "";
  const end = filterData.end_date
    ? `&revaluation_date[lte]=${filterData.end_date}`
    : "";
  const query = `/api/v1/company-assets/ppe/ppe-section?revalued=true&approved=true${start}${end}`;
  // function to fetch the data from the database
  const fetchData = async () => {
    let fetchingId;
    try {
      if (!filterData.start_date && !filterData.end_date) {
        feedback.error("Please select a date");
        return;
      }
      if (filterData.start_date || filterData.end_date) {
        fetchingId = toast.loading("Loading revalued assets...");
        const res = await API.get(query);
        toast.remove(fetchingId);
        if (res.data.length === 0) {
          feedback.info("No asset was revalued within the selected period");
          return;
        }

        if (res.data.length > 0) {
          setData(res.data);
          feedback.info("Revalued assets successfully loaded");
        }
      }
    } catch (error) {
      toast.remove(fetchingId);
      toast.error(error.message);
    }
  };
  const rows = data
    ? data?.map((data) => {
        return createData(
          data._id,
          data.code,
          data.cost,
          data.accumulated_depreciation,
          data.revalued_amount,
          moment(data.revaluation_date).format("YYYY-MM-DD")
        );
      })
    : [];
  return (
    <div style={{ height: "83vh" }}>
      <div
        style={{
          fontSize: "14px",
          marginBottom: "10px",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          columnGap: "10px",
          // alignItems: "center",
          // justifyContent: "space-between",
        }}>
        <Typography
          variant='body1'
          htmlFor='start-date'
          style={{ color: `${mode === "dark" ? "#0883FF" : "#116149"}` }}>
          Start Date
        </Typography>
        <TextField
          size='small'
          id='start-date'
          style={{ backgroundColor: "#ccc", borderRadius: "5px" }}
          type='date'
          value={filterData.start_date}
          onChange={(e) =>
            setFilterData({ ...filterData, start_date: e.target.value })
          }
        />
        <Typography
          variant='body1'
          htmlFor='end-date'
          style={{ color: `${mode === "dark" ? "#0883FF" : "#116149"}` }}>
          End Date
        </Typography>
        <TextField
          size='small'
          id='end-date'
          style={{ backgroundColor: "#ccc", borderRadius: "5px" }}
          type='date'
          value={filterData.end_date}
          onChange={(e) =>
            setFilterData({ ...filterData, end_date: e.target.value })
          }
        />
        <Button
          onClick={fetchData}
          style={{
            marginLeft: "10px",
            textTransform: "none",
            backgroundColor: `${mode === "dark" ? "#0883FF" : "#116149"}`,
          }}
          variant='contained'>
          Generate Report
        </Button>
      </div>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={message}
        autoHideDuration={6000}>
        <Alert severity='info'>
          Loading <span className='blinking-text'>.....</span>
        </Alert>
      </Snackbar>
      <Divider style={{ marginBottom: "10px" }} />
      <Paper sx={{ width: "100%", height: "70vh" }}>
        <DataGrid
          columns={columns}
          rows={rows}
          style={{
            backgroundColor: `${mode === "dark" ? "#101418" : "#ffffff"}`,
            color: `${mode === "dark" ? "#fff" : "#000"}`,
          }}
          sx={{
            boxShadow: 2,
            border: 1,
            "& .MuiDataGrid-cell:hover": {
              color: "primary.main",
            },
            "& .MuiDataGrid-footerCell": {
              backgroundColor: "primary.main",
            },
            "& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell": {
              backgroundColor: mode === "light" ? "#fafafa" : "#1d1d1d",
              borderBottom: `1px solid ${
                mode === "light" ? "#f0f0f0" : "#303030"
              }`,
            },
            "& .MuiDataGrid-columnHeader, .MuiDataGrid-cell": {
              borderRight: `1px solid ${
                mode === "light" ? "#f0f0f0" : "#303030"
              }`,
            },
            "& .MuiDataGrid-cell": {
              color:
                mode === "light" ? "rgba(0,0,0,.85)" : "rgba(255,255,255,0.65)",
            },
          }}
          slots={{ toolbar: GridToolbar, pagination: CustomPagination }}
        />
      </Paper>
    </div>
  );
}
